import React, { useEffect, useState } from "react";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";

const STORAGE_KEY = "rrdc_parent_gate";

/**
 * Wrap parent-facing routes so a shared access code is required before
 * anything loads. The code is validated by the backend; a short-lived JWT
 * is stored in localStorage and attached as x-parent-gate on all API calls.
 */
export default function ParentGate({ children }) {
  const [state, setState] = useState("checking"); // checking | prompt | ok
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem(STORAGE_KEY);
    if (!token) {
      setState("prompt");
      return;
    }
    // Verify token is still valid
    api
      .get("/parent-gate/check")
      .then(() => setState("ok"))
      .catch(() => {
        localStorage.removeItem(STORAGE_KEY);
        setState("prompt");
      });
  }, []);

  const submit = async (e) => {
    e && e.preventDefault && e.preventDefault();
    if (!password.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/parent-gate/verify", { password: password.trim() });
      localStorage.setItem(STORAGE_KEY, data.token);
      setState("ok");
      toast.success("Welcome");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (state === "ok") return children;
  if (state === "checking") {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center text-[#6B7280] text-sm">
        Checking access
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col">
      <header className="w-full bg-gradient-to-r from-[#2F50A1] to-[#3E63B5] py-4 px-6 shadow-sm">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <img
            src={`${API_BASE}/branding/logo`}
            alt="RRDC"
            className="w-9 h-9 rounded-full bg-white p-1 object-contain"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
          <div className="text-white">
            <div className="text-[10px] uppercase tracking-widest opacity-85">Rolling River Day Camp</div>
            <div className="text-base font-semibold">Trip Sign Ups</div>
          </div>
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-4">
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="w-full max-w-md rrdc-card p-6 space-y-4"
          data-testid="parent-gate-form"
        >
          <div className="w-12 h-12 rounded-full bg-[#EEF2FF] flex items-center justify-center">
            <Lock className="text-[#2F50A1]" size={22} />
          </div>
          <div>
            <h1 className="rrdc-heading text-xl font-bold text-[#111827]">Access required</h1>
            <p className="text-sm text-[#4B5563] mt-1 no-hyphens">
              Please enter the parent access code provided by the camp office to view the trip sign up pages.
            </p>
          </div>
          <div>
            <Label htmlFor="gate-password">Access code</Label>
            <Input
              id="gate-password"
              type="password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-12 mt-1"
              placeholder="Enter access code"
              data-testid="parent-gate-input"
              autoComplete="off"
            />
          </div>
          <Button
            type="submit"
            disabled={busy || !password.trim()}
            className="w-full h-12 rrdc-btn-primary rounded-xl font-semibold"
            data-testid="parent-gate-submit"
          >
            {busy ? "Checking" : "Continue"}
          </Button>
        </motion.form>
      </main>
    </div>
  );
}
